import React, { useEffect, useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { Box, Button, CssBaseline, Typography } from '@mui/material'
import { downloadTrxStream } from 'api/trx'
import { getTrxLogsPaged } from 'api/trx'
import { TEST_IDS } from 'test-ids'
import { useTranslation } from 'react-i18next'
import useDebounce from 'hooks/util/debounce'
import { getNextLogFile, type LogFile } from './next-file'
import { downloadUbjFile } from 'api/ubj'
import {
  buildLogLink,
  getLineKey,
  getLogLinkForSelection,
  getSelectedLines,
  type LineSelection,
} from './log-link'

type LogViewerProps = {
  hardwareId?: string | null
  onClose: () => void
  file?: {
    id: number | string
    year: number
  }
  instanceId?: number
  recordNumber?: number
  recordNumbers?: Array<number | string>
  startLine?: number
  endLine?: number
  // Enables copying a deep link; only admin pages that read the link pass it.
  tenantId?: string
  deferPopup?: boolean
  children?: React.ReactNode
}

type LogContentLine = {
  text: string
  color: string
  level: number
  tag: string
  message: string
  recordNumber: number | string
  lineNumber: number
  instanceId?: number
}

const LOG_LEVELS = {
  VERBOSE: 0,
  DEBUG: 1,
  INFO: 2,
  MAIN: 3,
  WARNING: 4,
  ERROR: 5,
  CRITICAL: 6,
}
const rx =
  //@ts-expect-error named groups in regex
  /^(?<level>[!EWIMDV]) \((?<timestamp>[^\)]+)\) (?<tag>[^:]+?)\s*: (?<message>.*)$/
export default function LogViewerComponent(
  props: LogViewerProps,
): React.ReactElement | null {
  const {
    hardwareId,
    onClose,
    instanceId,
    recordNumber,
    recordNumbers,
    startLine,
    endLine,
    file,
    tenantId,
    deferPopup = false,
  } = props
  const fileId = file?.id
  const fileYear = file?.year
  const recordNumbersKey = JSON.stringify(recordNumbers ?? [])
  const [container] = useState<HTMLDivElement | null>(() =>
    typeof document === 'undefined' ? null : document.createElement('div'),
  )
  const newWindow = useRef<Window | null>(null)
  const [shouldOpenPopup, setShouldOpenPopup] = useState(!deferPopup)
  const [emotionCache, setEmotionCache] = useState<ReturnType<
    typeof createCache
  > | null>(null)
  const onCloseRef = useRef(onClose)
  const [currentPage, setCurrentPage] = useState(1)
  const currentPageRef = useRef(1)
  const [fileList, setFileList] = useState<LogFile[]>([])
  const [rowCount, setRowCount] = useState(0)
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(
    null,
  )
  const requestKey = JSON.stringify([
    fileId,
    fileYear,
    hardwareId,
    instanceId,
    recordNumber,
    recordNumbersKey,
    currentPage,
  ])
  const loading =
    (!!hardwareId || (fileId !== undefined && fileYear !== undefined)) &&
    completedRequestKey !== requestKey
  const [logContent, setLogContent] = useState<LogContentLine[] | null>(null)
  const [filterLevel, setFilterLevel] = useState<number>(LOG_LEVELS.VERBOSE)
  const [filterTag, setFilterTag] = useState<string>('')
  const [tagList, setTagList] = useState<string[]>([''])
  const logContainerRef = useRef<HTMLDivElement | null>(null)
  const navigatedLineRangeRef = useRef<string | null>(null)
  const loadingNextFileRef = useRef(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [messageFilter, setMessageFilter] = useState<string>('')
  const [messageFilterInput, setMessageFilterInput] = useState<string>('')
  const PAGE_SIZE = 10

  // Passing a boolean `ignore` flag by value doesn't work here: it's
  // captured once when fetchData is called, so mutating the effect's local
  // variable in its cleanup never reaches the already-scheduled .then()
  // callback. A ref-based request generation counter is checked at
  // resolution time instead, so only the most recent request's response
  // (and its loading-state update) is ever applied.
  const latestRequestIdRef = useRef(0)
  const { t } = useTranslation()

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])
  const debouncedMessageChange = useDebounce(
    (value: string) => setMessageFilter(value),
    400,
  )
  const formatTimestamp = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  const openPopup = () => {
    const popup = window.open('', '_blank', 'width=800,height=860')
    if (!popup) return
    newWindow.current = popup
    setShouldOpenPopup(true)
  }

  useEffect(() => {
    if (container && shouldOpenPopup) {
      if (!newWindow.current) {
        newWindow.current = window.open('', '_blank', 'width=800,height=860')
      }
      const currentWindow = newWindow.current
      if (!currentWindow) {
        onCloseRef.current()
        return
      }
      setEmotionCache(
        createCache({
          key: 'mui-popup',
          container: currentWindow.document.head,
          prepend: true,
        }),
      )
      currentWindow.document.body.appendChild(container)
      const closeCheck = window.setInterval(() => {
        if (currentWindow.closed) {
          window.clearInterval(closeCheck)
          onCloseRef.current()
        }
      }, 500)
      return () => {
        window.clearInterval(closeCheck)
        currentWindow?.close()
      }
    }
  }, [container, shouldOpenPopup])

  const processData = useCallback(
    (
      rawData,
      recordNumber,
      lineInstanceId?,
      isCurrentRequest: () => boolean = () => true,
    ) => {
      return rawData.text().then((text) => {
        if (!isCurrentRequest()) return
        // eslint-disable-next-line no-control-regex
        const lines = text.replace(/\u001b\[[0-9;]*m/g, '').split(/\r?\n/)
        const formattedLines: LogContentLine[] = []
        for (let lineIndex = lines.length - 1; lineIndex >= 0; lineIndex--) {
          let line = lines[lineIndex]
          let color: string = 'lightgray'
          let level = LOG_LEVELS.VERBOSE
          let parts = line.match(rx)
          if (parts === null) {
            line = line.replace(/\[0;33m/g, '')
            parts = line.match(rx)
          }
          let timestamp = parts?.groups?.timestamp ?? ''
          try {
            if (timestamp !== '') {
              if (timestamp.includes(':')) {
                timestamp = `??-??-?? ${timestamp}`
              } else if (!isNaN(Number(timestamp))) {
                const timestampMs = Number(timestamp)
                const date = new Date(timestampMs)
                if (date.getUTCFullYear() < 1980) {
                  const hours = Math.floor(timestampMs / 3_600_000)
                  const minutes = Math.floor(timestampMs / 60_000) % 60
                  const seconds = Math.floor(timestampMs / 1_000) % 60
                  timestamp = `Δ BOOT + ${[hours, minutes, seconds]
                    .map((part) => String(part).padStart(2, '0'))
                    .join(':')}`
                } else {
                  timestamp = formatTimestamp.format(date).replace(',', '')
                }
              }
            }
          } catch (e) {
            console.error('Error parsing timestamp:', e)
          }
          switch (parts?.groups?.level) {
            case '!':
              color = 'purple'
              level = LOG_LEVELS.CRITICAL
              break
            case 'E':
              color = 'red'
              level = LOG_LEVELS.ERROR
              break
            case 'W':
              color = 'orange'
              level = LOG_LEVELS.WARNING
              break
            case 'M':
              color = 'lightblue'
              level = LOG_LEVELS.MAIN
              break
            case 'I':
              color = 'green'
              level = LOG_LEVELS.INFO
              break
            case 'D':
              color = 'gray'
              level = LOG_LEVELS.DEBUG
              break
          }
          if (parts?.groups?.tag) {
            setTagList((tags) =>
              !isCurrentRequest() || tags.includes(parts.groups.tag)
                ? tags
                : [...tags, parts.groups.tag],
            )
          }
          formattedLines.push({
            text: parts
              ? `${parts?.groups?.level ?? ''} (${timestamp}) ${parts?.groups?.tag ?? ''}: ${parts?.groups?.message ?? ''}`
              : JSON.stringify(line),
            color: color,
            level: level,
            tag: parts?.groups?.tag,
            message: parts?.groups?.message || JSON.stringify(line),
            recordNumber: recordNumber,
            lineNumber: lineIndex + 1,
            instanceId: lineInstanceId,
          })
        }
        setLogContent((currentLogContent) =>
          isCurrentRequest()
            ? (currentLogContent || []).concat(formattedLines)
            : currentLogContent,
        )
      })
    },
    [],
  )

  const loadRecord = useCallback(
    (
      instanceId: number,
      serialNumber: string,
      recordNumber: number | string,
      fileType: string,
      isCurrentRequest: () => boolean = () => true,
    ) => {
      return downloadTrxStream(
        instanceId,
        serialNumber,
        recordNumber,
        fileType,
      ).then((res) => {
        if (!isCurrentRequest()) return
        return processData(res.data, recordNumber, instanceId, isCurrentRequest)
      })
    },
    [processData],
  )

  useEffect(() => {
    const requestId = ++latestRequestIdRef.current
    let active = true
    const isCurrentRequest = () =>
      active && latestRequestIdRef.current === requestId
    Promise.resolve().then(() => {
      if (isCurrentRequest()) setCompletedRequestKey(null)
    })
    if (fileId !== undefined && fileYear !== undefined) {
      loadingNextFileRef.current = true
      Promise.resolve()
        .then(() => {
          if (!isCurrentRequest()) return
          setLogContent(null)
          return downloadUbjFile(fileId, fileYear)
        })
        .then((res) => {
          if (!res || !isCurrentRequest()) return
          return processData(res.data, fileId, undefined, isCurrentRequest)
        })
        .catch(console.error)
        .finally(() => {
          if (isCurrentRequest()) {
            loadingNextFileRef.current = false
            setCompletedRequestKey(requestKey)
          }
        })
    } else if (
      hardwareId &&
      instanceId !== undefined &&
      recordNumbersKey !== '[]'
    ) {
      loadingNextFileRef.current = true
      Promise.resolve()
        .then(async () => {
          if (!isCurrentRequest()) return
          setLogContent(null)
          const requestedRecords: Array<number | string> =
            JSON.parse(recordNumbersKey)
          for (const requestedRecord of requestedRecords) {
            if (!isCurrentRequest()) return
            await loadRecord(
              instanceId,
              hardwareId,
              requestedRecord,
              'log',
              isCurrentRequest,
            )
          }
        })
        .catch(console.error)
        .finally(() => {
          if (isCurrentRequest()) {
            loadingNextFileRef.current = false
            setCompletedRequestKey(requestKey)
          }
        })
    } else if (hardwareId) {
      loadingNextFileRef.current = true
      getTrxLogsPaged(currentPage, PAGE_SIZE, hardwareId, {
        ...(instanceId != null ? { instanceId } : {}),
        ...(currentPage === 1 && recordNumber != null ? { recordNumber } : {}),
      })
        .then((res) => {
          if (!isCurrentRequest()) return
          const { results, pagination } = res.data
          const files: LogFile[] = results.map((row) => ({
            instanceId: row.instanceId,
            serialNumber: row.serialNumber.trim(),
            recordNumber: row.recordNumber,
            fileType: 'log',
          }))
          currentPageRef.current = pagination.page
          setRowCount(pagination.total)
          const requestedIndex =
            currentPage === 1 && recordNumber != null
              ? files.findIndex(
                  (file) => String(file.recordNumber) === String(recordNumber),
                )
              : -1
          const index = requestedIndex >= 0 ? requestedIndex : 0
          setFileList(files)
          setCurrentIndex(index)
          if (files.length > 0) {
            const firstFile = files[index]
            return loadRecord(
              firstFile.instanceId,
              firstFile.serialNumber,
              firstFile.recordNumber,
              firstFile.fileType,
              isCurrentRequest,
            ).finally(() => {
              if (isCurrentRequest()) {
                loadingNextFileRef.current = false
                setCompletedRequestKey(requestKey)
              }
            })
          } else {
            loadingNextFileRef.current = false
            setCompletedRequestKey(requestKey)
          }
        })
        .catch(() => {
          if (isCurrentRequest()) {
            loadingNextFileRef.current = false
            setCompletedRequestKey(requestKey)
          }
        })
    }
    return () => {
      active = false
    }
  }, [
    currentPage,
    hardwareId,
    loadRecord,
    instanceId,
    recordNumber,
    recordNumbersKey,
    fileId,
    fileYear,
    processData,
    requestKey,
  ])

  const filteredLogContent =
    logContent?.filter(
      (line) =>
        (filterTag === '' || line?.tag === filterTag) &&
        line.level >= filterLevel &&
        (messageFilter === '' || line?.message?.includes(messageFilter)),
    ) || null

  const sourceKey = JSON.stringify([
    hardwareId,
    instanceId,
    fileId,
    fileYear,
    recordNumber,
    recordNumbersKey,
  ])
  const requestedRecords: Array<number | string> = JSON.parse(recordNumbersKey)
  const anchorLine = startLine ?? endLine
  const focusLine = endLine ?? startLine
  const firstRecord =
    fileId ??
    requestedRecords[0] ??
    recordNumber ??
    logContent?.[0]?.recordNumber
  const lastRecord =
    fileId ?? requestedRecords[requestedRecords.length - 1] ?? firstRecord
  const requestedSelection: LineSelection | null =
    anchorLine !== undefined &&
    focusLine !== undefined &&
    firstRecord !== undefined
      ? {
          anchor: getLineKey(firstRecord, anchorLine),
          focus: getLineKey(lastRecord, focusLine),
        }
      : null
  const [userSelection, setUserSelection] = useState<
    (LineSelection & { sourceKey: string }) | null
  >(null)
  const selection =
    userSelection?.sourceKey === sourceKey ? userSelection : requestedSelection
  const selectedLines = getSelectedLines(logContent ?? [], selection)
  const selectedLineKeys = new Set(
    selectedLines.map((line) => getLineKey(line.recordNumber, line.lineNumber)),
  )
  const shareLink = getLogLinkForSelection({
    tenantId,
    selectedLines,
    file,
    hardwareId,
    instanceId,
  })
  const shareUrl = shareLink
    ? buildLogLink(window.location.origin, shareLink)
    : null
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)

  const selectLine = (line: LogContentLine, extend: boolean) => {
    const key = getLineKey(line.recordNumber, line.lineNumber)
    setUserSelection(
      extend && selection
        ? { sourceKey, anchor: selection.anchor, focus: key }
        : { sourceKey, anchor: key, focus: key },
    )
  }

  const copyLink = () => {
    if (!shareUrl) return
    const clipboard =
      newWindow.current?.navigator?.clipboard ?? navigator.clipboard
    clipboard
      .writeText(shareUrl)
      .then(() => setCopiedUrl(shareUrl))
      .catch(console.error)
  }

  const requestedAnchor = requestedSelection?.anchor
  const requestedFocus = requestedSelection?.focus
  useEffect(() => {
    if (loading || !requestedAnchor) return
    const rangeKey = JSON.stringify([
      sourceKey,
      requestedAnchor,
      requestedFocus,
    ])
    if (navigatedLineRangeRef.current === rangeKey) return
    const target = Array.from(
      logContainerRef.current?.querySelectorAll<HTMLElement>(
        '[data-line-key]',
      ) ?? [],
    ).find((line) => line.dataset.lineKey === requestedAnchor)
    if (!target) return
    target.scrollIntoView({ block: 'center' })
    navigatedLineRangeRef.current = rangeKey
  }, [
    loading,
    logContent,
    emotionCache,
    filterLevel,
    filterTag,
    messageFilter,
    sourceKey,
    requestedAnchor,
    requestedFocus,
  ])

  const handleScroll = useCallback(() => {
    const scrollContainer = logContainerRef.current
    if (
      file ||
      recordNumbersKey !== '[]' ||
      !scrollContainer ||
      loadingNextFileRef.current ||
      !hardwareId
    )
      return
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer

    // Math.abs handles fractional pixel discrepancies on high-DPI screens
    const isBottom =
      scrollHeight > clientHeight &&
      scrollHeight - scrollTop - clientHeight <= 1

    if (!isBottom) return

    const next = getNextLogFile(
      currentPageRef.current,
      currentIndex,
      PAGE_SIZE,
      rowCount,
      fileList,
    )
    if (!next) return

    loadingNextFileRef.current = true
    setCurrentIndex(next.index)
    if (!next.file) {
      setCurrentPage(next.page)
      return
    }

    const nextFile = next.file
    const requestId = latestRequestIdRef.current
    loadRecord(
      nextFile.instanceId,
      nextFile.serialNumber.trim(),
      nextFile.recordNumber,
      nextFile.fileType,
      () => latestRequestIdRef.current === requestId,
    ).finally(() => {
      if (latestRequestIdRef.current === requestId) {
        loadingNextFileRef.current = false
      }
    })
  }, [
    currentIndex,
    hardwareId,
    fileList,
    loadRecord,
    rowCount,
    file,
    recordNumbersKey,
  ])

  if (deferPopup && !shouldOpenPopup) {
    return (
      <Button onClick={openPopup} data-testid={TEST_IDS.trxLogOpenButton}>
        {t('view_logs')}
      </Button>
    )
  }

  return (
    container &&
    emotionCache &&
    createPortal(
      <CacheProvider value={emotionCache}>
        <CssBaseline />
        <Box
          sx={{
            p: 2,
            height: 'calc(100vh - 16px)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <h2>{t('log_viewer', { hardwareId, instanceId })}</h2>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'flex-end',
                my: 1,
                height: '150px',
                flexDirection: 'row',
              }}
            >
              <div
                style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography variant="caption">{t('level')}</Typography>
                  <select
                    aria-label={t('level')}
                    //size="small"
                    value={filterLevel}
                    data-testid={TEST_IDS.trxLogLevelSelect}
                    onChange={(event: any) => {
                      const target = event.target as HTMLSelectElement
                      setFilterLevel(Number(target.value))
                    }}
                  >
                    {Object.entries(LOG_LEVELS).map(([key, value]) => (
                      <option key={key} value={value}>
                        {t(key)}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography variant="caption">{t('tag')}</Typography>
                  <select
                    //size="small"
                    value={filterTag}
                    data-testid={TEST_IDS.trxLogTagSelect}
                    onChange={(event: any) => {
                      const target = event.target as HTMLSelectElement
                      setFilterTag(target.value as string)
                    }}
                  >
                    {tagList.map((option) => (
                      <option key={option} value={option}>
                        {t(option)}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography variant="caption">{t('message')}</Typography>
                  <input
                    aria-label={t('message')}
                    type="text"
                    value={messageFilterInput}
                    data-testid={TEST_IDS.trxLogMessageInput}
                    onChange={(event: any) => {
                      const target = event.target as HTMLInputElement
                      setMessageFilterInput(target.value as string)
                      debouncedMessageChange(target.value as string)
                    }}
                  />
                </div>
                {tenantId ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!shareUrl}
                      onClick={copyLink}
                      data-testid={TEST_IDS.trxLogCopyLinkButton}
                    >
                      {t('copy_log_link')}
                    </Button>
                    <Typography variant="caption" role="status">
                      {copiedUrl && copiedUrl === shareUrl
                        ? t('log_link_copied')
                        : ''}
                    </Typography>
                  </div>
                ) : null}
              </div>
            </Box>
          </Box>
          {loading ? (
            <p>{t('loading')}</p>
          ) : (
            <>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <div
                  ref={logContainerRef}
                  style={{ height: '90%', overflowY: 'auto' }}
                  onScroll={handleScroll}
                  role="listbox"
                  aria-multiselectable="true"
                  aria-label={t('log_lines')}
                >
                  {filteredLogContent &&
                    filteredLogContent.map((line) => {
                      const lineKey = getLineKey(
                        line.recordNumber,
                        line.lineNumber,
                      )
                      const isSelected = selectedLineKeys.has(lineKey)
                      return (
                        <Box
                          component="p"
                          key={lineKey}
                          role="option"
                          tabIndex={0}
                          aria-selected={isSelected}
                          data-testid={TEST_IDS.trxLogContentLine}
                          data-line-key={lineKey}
                          data-record-number={line.recordNumber}
                          data-line-number={line.lineNumber}
                          data-log-selected={isSelected}
                          onClick={(event: React.MouseEvent) =>
                            selectLine(line, event.shiftKey)
                          }
                          onKeyDown={(event: React.KeyboardEvent) => {
                            if (event.key !== 'Enter' && event.key !== ' ') {
                              return
                            }
                            event.preventDefault()
                            selectLine(line, event.shiftKey)
                          }}
                          sx={{
                            color: line.color,
                            cursor: 'pointer',
                            backgroundColor: isSelected
                              ? 'action.selected'
                              : undefined,
                          }}
                        >
                          {line.text}
                        </Box>
                      )
                    })}
                </div>
              </Box>
            </>
          )}
        </Box>
      </CacheProvider>,
      container,
    )
  )
}
